import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { GPS } from "../../../../util/geo";
import { More } from "../../../components/MoreToggle";
import { OSMService, Voorziening } from "../../../services/osmService";
import { Stats } from "../../../state/types";
import { FetchState } from "../../../state/util";
import { KV } from "../util";

/*
  Statistieken:

  afstand_tot_huisartsenpraktijk: 0.4,
  afstand_tot_apotheek: 0.5,
  ziekenhuis_incl_buitenpolikliniek_afstand_tot_ziekenhuis: 1.3,
  ziekenhuis_excl_buitenpolikliniek_afstand_tot_ziekenhuis: 1.3,

  // boodschappen
  afstand_tot_grote_supermarkt: 0.3,
  afstand_tot_ov_dagel_levensmiddelen: 0.1,

  // winkels
  afstand_tot_warenhuis: 0.9,

  // horeca
  afstand_tot_café_ed: 0.2,

  afstand_tot_cafetaria_ed: 0.1,
  afstand_tot_restaurant: 0.1,
  afstand_tot_hotel_ed: 0.2,

  // scholen
  afstand_tot_kinderdagverblijf: 0.6,
  afstand_tot_buitenschoolse_opvang: 0.6,
  basisonderwijs_afstand_tot_school: 0.5,
  voortgezet_onderwijs_totaal_afstand_tot_school: 0.9,
  vmbo_afstand_tot_school: 1,
  havo_vwo_afstand_tot_school: 0.9,

  // cultuur
  afstand_tot_bibliotheek: 0.8,
  afstand_tot_zwembad: 2.4,
  afstand_tot_museum: 0.4,
  afstand_tot_podiumkunsten_totaal: 0.7,
  afstand_tot_poppodium: 0.9,
  afstand_tot_bioscoop: 0.7,
 */

function statisticsKeywords(stats: Stats) {
  const keywords = [];
  if (stats.afstand_tot_huisartsenpraktijk < 1) keywords.push("Arts");
  if (stats.ziekenhuis_excl_buitenpolikliniek_afstand_tot_ziekenhuis < 2)
    keywords.push("Ziekenhuis");
  if (stats.afstand_tot_grote_supermarkt < 1 || stats.afstand_tot_ov_dagel_levensmiddelen < 1)
    keywords.push("Boodschappen");
  if (stats.afstand_tot_warenhuis < 2) keywords.push("Winkel");
  if (stats["afstand_tot_café_ed"] < 1) keywords.push("Horeca");
  if (stats.afstand_tot_cafetaria_ed < 2 || stats.afstand_tot_restaurant)
    keywords.push("Restaurant");
  if (stats.afstand_tot_hotel_ed < 1) keywords.push("Hotel");
  if (
    stats.basisonderwijs_afstand_tot_school < 1 ||
    stats.voortgezet_onderwijs_totaal_afstand_tot_school < 1
  )
    keywords.push("School");
  if (
    stats.afstand_tot_museum < 1 ||
    stats.afstand_tot_podiumkunsten_totaal < 1 ||
    stats.afstand_tot_poppodium < 1
  )
    keywords.push("Cultuur");
  return keywords;
}

// OSM tag value -> Dutch category label. Unknown values fall back to `humanize`.
const KIND_LABELS: Record<string, string> = {
  // amenity
  place_of_worship: "Religie",
  school: "School",
  kindergarten: "Kinderopvang",
  university: "Onderwijs",
  college: "Onderwijs",
  hospital: "Ziekenhuis",
  doctors: "Huisarts",
  pharmacy: "Apotheek",
  dentist: "Tandarts",
  bank: "Bank",
  atm: "Pinautomaat",
  post_office: "Postkantoor",
  restaurant: "Restaurant",
  cafe: "Café",
  bar: "Café",
  pub: "Café",
  fast_food: "Snackbar",
  fuel: "Tankstation",
  parking: "Parkeren",
  community_centre: "Buurthuis",
  townhall: "Gemeentehuis",
  library: "Bibliotheek",
  theatre: "Theater",
  cinema: "Bioscoop",
  arts_centre: "Cultuurcentrum",
  police: "Politie",
  fire_station: "Brandweer",
  marketplace: "Markt",
  social_facility: "Zorg",
  // leisure
  park: "Park",
  playground: "Speeltuin",
  sports_centre: "Sporthal",
  pitch: "Sportveld",
  stadium: "Stadion",
  fitness_centre: "Sportschool",
  garden: "Tuin",
  swimming_pool: "Zwembad",
  nature_reserve: "Natuurgebied",
  // tourism
  museum: "Museum",
  attraction: "Attractie",
  hotel: "Hotel",
  guest_house: "Pension",
  hostel: "Hostel",
  viewpoint: "Uitzichtpunt",
  gallery: "Galerie",
  information: "Informatie",
  // common shops
  supermarket: "Supermarkt",
  bakery: "Bakker",
  convenience: "Buurtwinkel",
  butcher: "Slager",
  greengrocer: "Groenteboer",
  clothes: "Kledingwinkel",
  hairdresser: "Kapper",
  florist: "Bloemist",
};

const humanize = (kind: string) =>
  kind ? kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, " ") : "Overig";

const label = (kind: string) => {
  // OSM shop tags can be semicolon-joined (e.g. "bag;jewelry"); use the first.
  const first = (kind || "").split(";")[0];
  return KIND_LABELS[first] || humanize(first);
};

// Group POIs by category label, keeping categories ordered by their nearest POI.
function groupByCategory(pois: Voorziening[]): [string, Voorziening[]][] {
  const groups = new Map<string, Voorziening[]>();
  for (const poi of pois) {
    const key = label(poi.kind);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(poi);
  }
  // `pois` arrives sorted by distance, so each group's first item is its nearest.
  return [...groups.entries()].sort((a, b) => a[1][0].distanceM - b[1][0].distanceM);
}

const OSMVoorzieningen = ({ center }: { center: GPS }) => {
  const [state, setState] = useState<FetchState>("fetching");
  const [pois, setPois] = useState<Voorziening[]>([]);

  useEffect(() => {
    let active = true;
    setState("fetching");
    OSMService.voorzieningen(center)
      .then((data) => {
        if (!active) return;
        setPois(data);
        setState("fetched");
      })
      .catch(() => active && setState("error"));
    return () => {
      active = false;
    };
  }, [center]);

  if (state === "fetching")
    return (
      <div className="text-dark-gray text-sm p-2.5">
        <FontAwesomeIcon icon={faSpinner} spin={true} fill="green" /> Laden…
      </div>
    );
  if (state === "error")
    return <div className="text-dark-gray text-sm p-2.5">Kon voorzieningen niet laden.</div>;
  if (!pois.length)
    return <div className="text-dark-gray text-sm p-2.5">Geen voorzieningen in de buurt.</div>;

  return (
    <dl className="text-sm mt-1 divide-y divide-gray">
      {groupByCategory(pois).map(([category, items]) => (
        <div key={category} className="flex py-1 pl-6">
          <dt className="w-2/5">{category}</dt>
          <dd className="w-3/5">
            {items.map((poi, i) => (
              <div key={i}>
                {poi.name} <span className="text-dark-gray">· {poi.distanceM} m</span>
              </div>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
};

interface VoorzieningenProps {
  stats: Stats;
  center: GPS;
}
export const Voorzieningen = ({ stats, center }: VoorzieningenProps) => {
  const [open, setOpen] = useState(false);
  if (!stats) return null;
  const keywords = statisticsKeywords(stats);
  return (
    <>
      <KV k="Voorzieningen">
        <More open={open} setOpen={setOpen} />
        {keywords.join(". ") || "Beperkt"}.
      </KV>
      {open && <OSMVoorzieningen center={center} />}
    </>
  );
};
